import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import ButtonBase from 'design-system/button'
import { PainsType, ISymptomsCategoryResponse, ISymptonsResponse } from 'types'
import {
  addPatientPains,
  addPatientSymptoms,
  getSymptomsCategory,
  getSymptomsByCategory
} from 'services/symptoms_tracking'
import PainForm from './components/PainForm'
import SymptomsForm from './components/SymptomsForm'
import { useParams } from 'react-router-dom'
import { useToast } from 'services/context/toast'

interface SymptomsModalProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
}

const SymptomsModal: React.FC<SymptomsModalProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [activeTab, setActiveTab] = useState(0)

  // Pain form state
  const [painLevel, setPainLevel] = useState(1)
  const [painLocation, setPainLocation] = useState<PainsType>('Head')
  const [painDate, setPainDate] = useState<Dayjs | null>(dayjs())
  const [painTime, setPainTime] = useState<Dayjs | null>(dayjs())

  // Symptoms form state
  const [symptomStep, setSymptomStep] = useState<'category' | 'symptoms'>(
    'category'
  )
  const [symptomsCategories, setSymptomsCategories] = useState<
    ISymptomsCategoryResponse[]
  >([])
  const [selectedCategory, setSelectedCategory] =
    useState<ISymptomsCategoryResponse | null>(null)
  const [symptoms, setSymptoms] = useState<ISymptonsResponse[]>([])
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([])
  const [symptomDate, setSymptomDate] = useState<Dayjs | null>(dayjs())
  const [symptomTime, setSymptomTime] = useState<Dayjs | null>(dayjs())
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSymptoms, setLoadingSymptoms] = useState(false)
  const [loadingPains, setLoadingPains] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { patientId } = useParams<{ patientId: string }>()
  const toast = useToast()

  // Load symptoms categories when modal opens and symptoms tab is active
  useEffect(() => {
    if (open && activeTab === 1) {
      loadSymptomsCategories()
    }
  }, [open, activeTab])

  // Reset symptoms form when modal closes or tab changes
  useEffect(() => {
    if (!open || activeTab !== 1) {
      resetSymptomsForm()
    }
  }, [open, activeTab])

  const loadSymptomsCategories = async () => {
    try {
      setLoadingCategories(true)
      setError(null)
      const response = await getSymptomsCategory()
      if (response.success && response.data) {
        setSymptomsCategories(response.data)
      } else {
        setError('Erro ao carregar categorias de sintomas')
      }
    } catch (error) {
      console.error('Error loading symptoms categories:', error)
      setError('Erro ao carregar categorias de sintomas')
    } finally {
      setLoadingCategories(false)
    }
  }

  const loadSymptomsByCategory = async (categoryId: string) => {
    try {
      setLoadingSymptoms(true)
      setError(null)
      const response = await getSymptomsByCategory(categoryId)
      if (response.success && response.data) {
        setSymptoms(response.data)
        setSymptomStep('symptoms')
      } else {
        setError('Erro ao carregar sintomas da categoria')
      }
    } catch (error) {
      console.error('Error loading symptoms by category:', error)
      setError('Erro ao carregar sintomas da categoria')
    } finally {
      setLoadingSymptoms(false)
    }
  }

  const resetSymptomsForm = () => {
    setSymptomStep('category')
    setSelectedCategory(null)
    setSymptoms([])
    setSelectedSymptoms([])
    setSymptomDate(dayjs())
    setSymptomTime(dayjs())
    setError(null)
  }

  const handleCategorySelect = (category: ISymptomsCategoryResponse) => {
    setSelectedCategory(category)
  }

  const handleAdvanceToSymptoms = () => {
    if (selectedCategory) {
      loadSymptomsByCategory(selectedCategory.symptoms_category_id)
    }
  }

  const handleSymptomToggle = (symptomId: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptomId)
        ? prev.filter(id => id !== symptomId)
        : [...prev, symptomId]
    )
  }

  const handleBackToCategories = () => {
    setSymptomStep('category')
    setSelectedCategory(null)
    setSymptoms([])
    setSelectedSymptoms([])
    setError(null)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const mountDateTime = (date: Dayjs | null, time: Dayjs | null) => {
    if (!date?.isValid() || !time?.isValid()) return null

    const combined = date
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0)

    return combined.toISOString()
  }

  const handleSubmit = async () => {
    if (activeTab === 0) {
      // Pain form submission
      setLoadingPains(true)
      try {
        const response = await addPatientPains({
          patient_id: patientId || '',
          location: painLocation,
          severity: painLevel,
          event_date: mountDateTime(painDate, painTime)
        })

        if (!response.success) {
          toast.handler({
            severity: 'error',
            content: 'Erro ao registrar dor'
          })
          return
        }
      } catch (error) {
        console.error('Error submitting pain:', error)
        toast.handler({
          severity: 'error',
          content: 'Erro ao registrar dor'
        })
        return
      } finally {
        setLoadingPains(false)
      }
    } else {
      const response = await addPatientSymptoms(
        selectedSymptoms.map(id => ({
          symptoms_id: id,
          patient_id: patientId || '',
          event_date: mountDateTime(symptomDate, symptomTime),
          severity: 1
        }))
      )

      if (!response.success) {
        toast.handler({
          severity: 'error',
          content: 'Erro ao registrar sintomas'
        })
        return
      }
    }
    toast.handler({
      severity: 'success',
      content:
        activeTab === 0
          ? 'Dor registrada com sucesso!'
          : 'Sintoma registrado com sucesso!'
    })
    onSubmit()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>
            Registrar {activeTab === 0 ? 'Dor' : 'Sintoma'}
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant='fullWidth'>
          <Tab label='Dor' />
          <Tab label='Sintomas' />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 4 }}>
        {activeTab === 0 ? (
          <PainForm
            painLevel={painLevel}
            setPainLevel={setPainLevel}
            painLocation={painLocation}
            setPainLocation={setPainLocation}
            painDate={painDate}
            setPainDate={setPainDate}
            painTime={painTime}
            setPainTime={setPainTime}
          />
        ) : (
          <SymptomsForm
            step={symptomStep}
            categories={symptomsCategories}
            selectedCategory={selectedCategory}
            symptoms={symptoms}
            selectedSymptoms={selectedSymptoms}
            symptomDate={symptomDate}
            symptomTime={symptomTime}
            loadingCategories={loadingCategories}
            loadingSymptoms={loadingSymptoms}
            error={error}
            onCategorySelect={handleCategorySelect}
            onSymptomToggle={handleSymptomToggle}
            onBackToCategories={handleBackToCategories}
            onDateChange={setSymptomDate}
            onTimeChange={setSymptomTime}
            onAdvanceToSymptoms={handleAdvanceToSymptoms}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <ButtonBase onClick={onClose} color='inherit'>
          Cancelar
        </ButtonBase>

        {activeTab === 0 ? (
          <ButtonBase
            variant='contained'
            onClick={handleSubmit}
            disabled={
              !painDate?.isValid() || !painTime?.isValid() || loadingPains
            }
          >
            Registrar Dor
          </ButtonBase>
        ) : (
          <>
            {symptomStep === 'category' ? null : (
              <ButtonBase
                variant='contained'
                onClick={handleSubmit}
                disabled={
                  selectedSymptoms.length === 0 ||
                  !symptomDate?.isValid() ||
                  !symptomTime?.isValid() ||
                  loadingSymptoms
                }
              >
                Enviar
              </ButtonBase>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SymptomsModal
